// =============================================================================
// ApiClient — Type-safe HTTP client with retry, CSRF, and auth refresh
// =============================================================================
// Uses native fetch. Designed for browser environments.
// Cookies are used for auth (HttpOnly, managed by the server).
// CSRF token is read from a meta tag or cookie and sent as a header.
// =============================================================================

import type { ApiErrorResponse } from '@tkpod/shared';
import { ApiClientError } from './types';
import type { ApiClientConfig, RequestConfig, RequestOptions, HttpMethod } from './types';

/** HTTP status codes that are safe to retry */
const RETRYABLE_STATUS_CODES = new Set([408, 429, 502, 503, 504]);

/** Default configuration values */
const DEFAULTS = {
  credentials: 'include' as RequestCredentials,
  timeout: 30_000,
  maxRetries: 2,
  retryBaseDelay: 1_000,
} as const;

export class ApiClient {
  private readonly config: Required<
    Pick<ApiClientConfig, 'baseUrl' | 'credentials' | 'timeout' | 'maxRetries' | 'retryBaseDelay'>
  > &
    ApiClientConfig;

  /** Track whether a token refresh is in progress to avoid multiple concurrent refreshes */
  private refreshPromise: Promise<boolean> | null = null;

  /** Track whether a CSRF refresh is in progress */
  private csrfRefreshPromise: Promise<boolean> | null = null;

  /** BroadcastChannel for multi-tab refresh coordination */
  private bc: BroadcastChannel | null = null;

  constructor(config: ApiClientConfig) {
    this.config = {
      ...config,
      credentials: config.credentials ?? DEFAULTS.credentials,
      timeout: config.timeout ?? DEFAULTS.timeout,
      maxRetries: config.maxRetries ?? DEFAULTS.maxRetries,
      retryBaseDelay: config.retryBaseDelay ?? DEFAULTS.retryBaseDelay,
    };

    this.initBroadcastChannel();
  }

  /**
   * Coordinate token refresh across tabs.
   * When one tab refreshes successfully, it broadcasts 'TOKEN_REFRESHED'
   * so other tabs skip redundant refresh calls.
   * When a refresh fails terminally, it broadcasts 'SESSION_EXPIRED'.
   */
  private initBroadcastChannel(): void {
    if (typeof BroadcastChannel === 'undefined') return;
    try {
      this.bc = new BroadcastChannel('tkpod_auth');
      this.bc.onmessage = (event: MessageEvent<string>) => {
        if (event.data === 'TOKEN_REFRESHED') {
          this.config.onTokenRefreshed?.();
        } else if (event.data === 'SESSION_EXPIRED') {
          this.config.onSessionExpired?.();
        }
      };
    } catch {
      // BroadcastChannel not supported — single-tab fallback
    }
  }

  // ---------------------------------------------------------------------------
  // Public HTTP methods
  // ---------------------------------------------------------------------------

  async get<T>(path: string, options?: RequestOptions): Promise<T> {
    return this.request<T>({ method: 'GET', path, ...options });
  }

  async post<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>({ method: 'POST', path, body, ...options });
  }

  async put<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>({ method: 'PUT', path, body, ...options });
  }

  async patch<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>({ method: 'PATCH', path, body, ...options });
  }

  async delete<T>(path: string, options?: RequestOptions): Promise<T> {
    return this.request<T>({ method: 'DELETE', path, ...options });
  }

  // ---------------------------------------------------------------------------
  // Core request pipeline
  // ---------------------------------------------------------------------------

  private async request<T>(config: RequestConfig): Promise<T> {
    // Apply request interceptor
    let finalConfig = config;
    if (this.config.onRequest) {
      finalConfig = await this.config.onRequest(config);
    }

    const maxAttempts = finalConfig.noRetry ? 1 : this.config.maxRetries + 1;
    let lastError: ApiClientError | null = null;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        // Wait before retry (exponential backoff with jitter)
        if (attempt > 0) {
          const delay = this.calculateBackoff(attempt);
          await this.sleep(delay);
        }

        return await this.executeRequest<T>(finalConfig);
      } catch (error) {
        if (!(error instanceof ApiClientError)) {
          throw error;
        }

        lastError = error;

        // Handle 403 CSRF errors — fetch a fresh CSRF cookie and retry (once)
        if (
          error.statusCode === 403 &&
          error.errorCode === 'CSRF_VALIDATION_FAILED' &&
          attempt === 0
        ) {
          const refreshed = await this.handleCsrfRefresh();
          if (refreshed) continue;
        }

        // Handle 401 — attempt token refresh (once)
        // Skip for noRetry requests (e.g. login/register where 401 = bad credentials)
        if (error.statusCode === 401 && attempt === 0 && !finalConfig.noRetry && this.config.onUnauthorized) {
          const refreshed = await this.handleUnauthorized();
          if (refreshed) {
            // Retry the original request (this counts as the next attempt)
            continue;
          }
        }

        // Don't retry non-retryable errors
        if (!error.retryable) {
          break;
        }

        // Report error via interceptor
        this.config.onError?.(error);
      }
    }

    // All attempts exhausted
    if (lastError) {
      this.config.onError?.(lastError);
      throw lastError;
    }

    // Should not reach here
    throw new ApiClientError({
      message: 'Request failed with unknown error',
      statusCode: 0,
      errorCode: 'UNKNOWN_ERROR',
    });
  }

  private async executeRequest<T>(config: RequestConfig): Promise<T> {
    const url = this.buildUrl(config.path, config.query);
    const timeout = config.timeout ?? this.config.timeout;

    // Build headers
    const headers: Record<string, string> = {
      Accept: 'application/json',
      ...config.headers,
    };

    // Add Content-Type for requests with a JSON body. FormData must NOT carry
    // an explicit Content-Type — the browser appends the multipart boundary
    // itself, and overriding it breaks server-side parsing.
    const isFormData =
      typeof FormData !== 'undefined' && config.body instanceof FormData;
    if (config.body !== undefined && config.body !== null && !isFormData) {
      headers['Content-Type'] = 'application/json';
    }

    // Add CSRF token for state-changing methods
    if (this.isStateChanging(config.method)) {
      const csrfToken = this.getCsrfToken();
      if (csrfToken) {
        headers['X-CSRF-Token'] = csrfToken;
      }
    }

    // Setup timeout via AbortController
    const controller = new AbortController();
    const signal = config.signal
      ? this.combineSignals(config.signal, controller.signal)
      : controller.signal;

    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(url, {
        method: config.method,
        headers,
        body:
          config.body === undefined || config.body === null
            ? undefined
            : isFormData
              ? (config.body as FormData)
              : JSON.stringify(config.body),
        credentials: this.config.credentials,
        signal,
      });

      clearTimeout(timeoutId);

      // Invoke response interceptor
      if (this.config.onResponse) {
        await this.config.onResponse(response, config);
      }

      // Handle success
      if (response.ok) {
        // Handle 204 No Content
        if (response.status === 204) {
          return undefined as T;
        }

        const contentType = response.headers.get('content-type');
        if (contentType?.includes('application/json')) {
          return (await response.json()) as T;
        }

        // Non-JSON success response
        return (await response.text()) as unknown as T;
      }

      // Handle error responses
      const apiError = await this.parseErrorResponse(response);
      const correlationId = response.headers.get('x-correlation-id');

      throw new ApiClientError({
        message: apiError?.message ?? `Request failed with status ${response.status}`,
        statusCode: response.status,
        errorCode: apiError?.error ?? `HTTP_${response.status}`,
        correlationId,
        apiError,
        retryable: RETRYABLE_STATUS_CODES.has(response.status),
      });
    } catch (error) {
      clearTimeout(timeoutId);

      // Re-throw ApiClientError as-is
      if (error instanceof ApiClientError) {
        throw error;
      }

      // Handle abort/timeout
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw new ApiClientError({
          message: 'Request timed out',
          statusCode: 0,
          errorCode: 'TIMEOUT',
          retryable: true,
        });
      }

      // Handle network errors
      if (error instanceof TypeError) {
        throw new ApiClientError({
          message: `Network error: ${error.message}`,
          statusCode: 0,
          errorCode: 'NETWORK_ERROR',
          retryable: true,
        });
      }

      // Unknown error
      throw new ApiClientError({
        message: error instanceof Error ? error.message : 'Unknown error',
        statusCode: 0,
        errorCode: 'UNKNOWN_ERROR',
        retryable: false,
      });
    }
  }

  // ---------------------------------------------------------------------------
  // Auth refresh coordination
  // ---------------------------------------------------------------------------

  private async handleUnauthorized(): Promise<boolean> {
    if (!this.config.onUnauthorized) return false;

    // Deduplicate concurrent refresh attempts
    if (!this.refreshPromise) {
      this.refreshPromise = this.config.onUnauthorized()
        .then((ok) => {
          if (ok) {
            this.config.onTokenRefreshed?.();
            this.bc?.postMessage('TOKEN_REFRESHED');
          } else {
            // Broadcast to other tabs so they also redirect to login.
            // We do NOT call onSessionExpired on THIS tab because
            // onUnauthorized already handles redirect/cleanup when returning false.
            this.bc?.postMessage('SESSION_EXPIRED');
          }
          return ok;
        })
        .finally(() => {
          this.refreshPromise = null;
        });
    }

    return this.refreshPromise;
  }

  // ---------------------------------------------------------------------------
  // CSRF refresh coordination
  // ---------------------------------------------------------------------------

  private async handleCsrfRefresh(): Promise<boolean> {
    if (!this.csrfRefreshPromise) {
      this.csrfRefreshPromise = this.fetchCsrfToken().finally(() => {
        this.csrfRefreshPromise = null;
      });
    }
    return this.csrfRefreshPromise;
  }

  private async fetchCsrfToken(): Promise<boolean> {
    try {
      const url = this.buildUrl('/api/v1/auth/csrf');
      const response = await fetch(url, {
        method: 'GET',
        credentials: this.config.credentials ?? 'include',
      });
      return response.ok || response.status === 204;
    } catch {
      return false;
    }
  }

  // ---------------------------------------------------------------------------
  // URL building
  // ---------------------------------------------------------------------------

  private buildUrl(path: string, query?: Record<string, string | number | boolean | undefined>): string {
    const base = this.config.baseUrl.replace(/\/+$/, '');
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    const url = new URL(`${base}${cleanPath}`);

    if (query) {
      for (const [key, value] of Object.entries(query)) {
        if (value !== undefined && value !== null) {
          url.searchParams.set(key, String(value));
        }
      }
    }

    return url.toString();
  }

  // ---------------------------------------------------------------------------
  // CSRF
  // ---------------------------------------------------------------------------

  private getCsrfToken(): string | null {
    // Try meta tag first (server-rendered)
    if (typeof document !== 'undefined') {
      const meta = document.querySelector('meta[name="csrf-token"]');
      if (meta) {
        return meta.getAttribute('content');
      }

      // Fallback: read from cookie (double-submit pattern)
      const match = document.cookie.match(/(?:^|;\s*)__tk_csrf=([^;]*)/);
      if (match) {
        return decodeURIComponent(match[1]);
      }
    }

    return null;
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  private isStateChanging(method: HttpMethod): boolean {
    return method !== 'GET';
  }

  private async parseErrorResponse(response: Response): Promise<ApiErrorResponse | null> {
    try {
      const contentType = response.headers.get('content-type');
      if (contentType?.includes('application/json')) {
        return (await response.json()) as ApiErrorResponse;
      }
    } catch {
      // Failed to parse error body — return null
    }
    return null;
  }

  private calculateBackoff(attempt: number): number {
    // Exponential backoff with jitter: base * 2^(attempt-1) * (0.5..1.5)
    const base = this.config.retryBaseDelay * Math.pow(2, attempt - 1);
    const jitter = 0.5 + Math.random();
    return Math.min(base * jitter, 30_000); // Cap at 30s
  }

  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private combineSignals(userSignal: AbortSignal, timeoutSignal: AbortSignal): AbortSignal {
    // Use AbortSignal.any if available (modern browsers)
    if ('any' in AbortSignal) {
      return AbortSignal.any([userSignal, timeoutSignal]);
    }

    // Fallback: create a new controller that aborts when either signal fires
    const controller = new AbortController();
    const onAbort = () => controller.abort();
    userSignal.addEventListener('abort', onAbort, { once: true });
    timeoutSignal.addEventListener('abort', onAbort, { once: true });
    return controller.signal;
  }
}
